import { reportsPosition } from '../GscTools/metrics';
import { searchTypeLabel } from '../GscTools/period';
import { ASSUMED_PROVISIONAL_HOURS, REPORTING_TIMEZONE, formatWindow } from './hours';

/**
 * The CSV of one fetch: every hour of the window, with what is known about each.
 *
 * Four things travel with the numbers because without them an hourly row cannot be placed: the
 * exact timestamp including its offset, whether the hour was returned at all, whether this report
 * considers it settled, and the counterpart hour it was compared against. A spreadsheet of clicks
 * per hour with none of that is a file that will be read as a measurement of recent traffic,
 * which is exactly what a trailing provisional hour is not.
 *
 * The incomplete boundary is exported as what it is — an assumption of this report's, not a value
 * Search Console supplied — so a file that outlives this page cannot imply Google confirmed it.
 */
export function buildHoursCsv({ t, scope, window, baselineWindow, hours, fetchedAt }) {
    const hasPosition = reportsPosition(scope.searchType);

    const meta = [
        [t('tools.latest_hours.export.report'), t('tools.latest_hours.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [t('tools.latest_hours.export.window'), formatWindow(window)],
        [
            t('tools.latest_hours.export.baseline_window'),
            scope.comparing ? formatWindow(baselineWindow) : t('tools.gsc.comparison.none'),
        ],
        [t('tools.gsc.exports.data_state'), t('tools.latest_hours.export.data_state_value')],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [
            t('tools.latest_hours.export.boundary'),
            t('tools.latest_hours.export.boundary_value', { hours: ASSUMED_PROVISIONAL_HOURS }),
        ],
        [
            t('tools.latest_hours.export.coverage'),
            t(`tools.latest_hours.coverage_modes.${scope.coverage}`),
        ],
        [
            t('tools.latest_hours.export.comparable_hours'),
            hours.filter((hour) => hour.comparable).length,
        ],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [],
    ];

    const header = [
        t('tools.latest_hours.columns.hour'),
        t('tools.latest_hours.export.offset'),
        t('tools.latest_hours.columns.completeness'),
        t('tools.gsc.exports.state'),
        t('tools.gsc.metrics.clicks_short'),
        t('tools.gsc.metrics.impressions_short'),
        t('tools.gsc.metrics.ctr_short'),
        ...(hasPosition ? [t('tools.gsc.metrics.position_short')] : []),
        t('tools.latest_hours.export.prior_hour'),
        t('tools.latest_hours.export.prior_clicks'),
        t('tools.latest_hours.export.prior_impressions'),
        t('tools.latest_hours.export.comparable'),
    ];

    const body = hours.map((hour) => [
        hour.key,
        hour.hour.format('Z'),
        hour.provisional
            ? t('tools.latest_hours.state.provisional')
            : t('tools.latest_hours.state.complete'),
        hour.returned ? t('tools.gsc.exports.returned') : t('tools.gsc.not_returned'),
        hour.currentRow?.clicks,
        hour.currentRow?.impressions,
        hour.currentRow?.ctr,
        ...(hasPosition ? [hour.currentRow?.position] : []),
        scope.comparing ? hour.priorKey : '',
        scope.comparing ? hour.priorRow?.clicks : '',
        scope.comparing ? hour.priorRow?.impressions : '',
        hour.comparable
            ? t('tools.latest_hours.export.comparable_yes')
            : t(`tools.latest_hours.export.not_comparable.${notComparableReason(hour, scope)}`),
    ]);

    return [...meta, header, ...body];
}

/** Why one hour is out of the comparison, so the export never leaves a blank to be guessed at. */
function notComparableReason(hour, scope) {
    if (!scope.comparing) return 'no_comparison';
    if (!hour.returned) return 'not_returned';
    if (hour.provisional) return 'provisional';
    return 'no_prior';
}
